// Basiert auf https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev
import {Loader} from '@googlemaps/js-api-loader';
import {Runner} from './models/runner';
import PathProvider from './providers/path';
import {
    RUNS,
    RUN_FULL,
    WAYPOINTS,
    Waypoint,
    findWaypointForPosition,
} from './models/waypoint';
import RunnerIconProvider from './providers/runner-icon';
import initRunnersStore, {RunnersStore} from './store/runners';
import RunProvider, {LANGUAGE_DE} from './providers/run';

export type WaypointMarker = {waypoint: Waypoint, marker: google.maps.Marker};

class RaceMap {
    googleMap;

    pathProvider;

    runnerIconProvider;

    run = RUN_FULL;

    runnerMarkers = new Map<string, google.maps.Marker>();

    runnerMarkersLock = new Map<string, boolean>();

    waypointMarkers: Array<WaypointMarker> = [];

    runPaths = new Map<string, google.maps.Polyline>();

    geolocationMarker;

    FOLLOW_DISABLED = 'disabled';

    FOLLOW_RUNNER = 'runner';

    FOLLOW_GEOLOCATION = 'geolocation';

    follow = this.FOLLOW_DISABLED;

    showGeolocationButton = true;

    lastGeolocationPosition = null;

    private readonly mapElement: Element;

    private toggleFollowRunnerButton: Element;

    private runProvider: RunProvider;

    private favoriteRunners: RunnersStore;

    private runners: Array<Runner>;

    private updateRunnerMarkersInterval: ReturnType<typeof setInterval>;

    constructor(GoogleMap) {
        this.mapElement = document.querySelector('.js-race-tracking-map');
        if (!this.mapElement) {
            return;
        }

        this.pathProvider = new PathProvider();
        this.runProvider = RunProvider.getInstance();

        this.runProvider.setLanguage(window.config.language || LANGUAGE_DE);

        this.favoriteRunners = initRunnersStore();

        this.run = this.favoriteRunners.getState().run;

        this.googleMap = new GoogleMap(this.mapElement, {
            center: {
                lat: 50.938,
                lng: 6.956,
            },
            minZoom: 9,
            maxZoom: 20,
            disableDefaultUI: true,
            zoomControl: false,
        });

        this.toggleFollowRunnerButton = document.querySelector('.js-race-tracking-activate-follow-runner');
    }

    init() {
        if (!this.googleMap) {
            throw new Error('No Map element found');
        }

        // Follow deaktivieren bei Drag Start
        this.googleMap.addListener('dragstart', () => {
            this.toggleFollowRunner(false);
        });

        this.addPaths();
        this.addWaypoints();
        this.setBounds();

        this.initRunners(() => {
            this.mapElement.classList.remove('race-tracking__map--disabled');

            this.initFollowRunner();
            this.initGeolocationMarker();
        });
    }

    async initRunners(initMap) {
        this.runners = this.favoriteRunners.getState().runners;
        const hasRunners = this.runners.length > 0;

        if (hasRunners) {
            initMap();
        }

        await this.matchRunnerMarkersWithRunners();
        this.updateRunnerMarkerPositions();
        this.updateVisibleRunnerMarkers();
        this.animateCameraFollow();

        this.updateRunnerMarkersInterval = setInterval(() => {
            this.updateRunnerMarkerPositions();
            this.updateVisibleRunnerMarkers();
            this.animateCameraFollow();
        }, 1000);

        this.favoriteRunners.subscribe(async ({runners}) => {
            const runnersHaveChanged = !RaceMap.compareRunnersArrays(this.runners, runners);

            this.runners = runners;

            if (!this.runners) return;

            if (this.runners && !hasRunners) {
                initMap();
            }

            this.updateWaypoints();
            this.updatePaths();

            await this.matchRunnerMarkersWithRunners();
            this.updateRunnerMarkerPositions();
            this.updateVisibleRunnerMarkers();
            this.animateCameraFollow();

            if (runnersHaveChanged) {
                this.setBounds();
            }
        });
    }

    static compareRunnersArrays(arrayA, arrayB) {
        const arrayToString = (array) => array.map(({startNumber}) => startNumber).toString();
        return arrayToString(arrayA) === arrayToString(arrayB);
    }

    addPaths() {
        const {run: currentRun} = this.favoriteRunners.getState();

        RUNS.forEach((run) => {
            const runPath = new google.maps.Polyline({
                path: this.pathProvider.getPathPositionsForRun(run),
                strokeColor: '#DA636B',
                strokeWeight: 5,
                geodesic: true,
                clickable: false,
                visible: run === currentRun,
                map: this.googleMap,
            });

            this.runPaths.set(run, runPath);
        });
    }

    updatePaths() {
        const {run} = this.favoriteRunners.getState();
        this.runPaths.forEach((path, key) => {
            path.setVisible(key === run);
        });
    }

    addWaypoints() {
        const {run} = this.favoriteRunners.getState();

        const waypoints = this.runners ? this.runners[0].waypoints : WAYPOINTS();

        waypoints.forEach(async (initialData) => {
            const waypoint = new Waypoint(initialData);
            const marker = new google.maps.Marker(waypoint.getMarkerOptions({
                visible: waypoint.isUsedForRun(run),
                map: this.googleMap,
            }));

            this.waypointMarkers.push({
                waypoint,
                marker,
            });
        });
    }

    updateWaypoints() {
        const {run} = this.favoriteRunners.getState();

        this.waypointMarkers.forEach(({
            waypoint,
            marker,
        }, index) => {
            const newWaypoint = findWaypointForPosition(
                this.runners?.[0]?.waypoints,
                waypoint.position,
            ) || waypoint;
            this.waypointMarkers[index].waypoint = newWaypoint;

            marker.setVisible(newWaypoint.isUsedForRun(run));
            marker.setIcon(newWaypoint.getMarkerIcon());
        });
    }

    setBounds() {
        const {run} = this.favoriteRunners.getState();
        const markers = this.pathProvider.getPathPositionsForRun(run);
        const bounds = new google.maps.LatLngBounds();
        markers.forEach((position) => {
            bounds.extend(position);
        });

        this.googleMap.setCenter(bounds.getCenter());
        this.googleMap.fitBounds(bounds);
    }

    initFollowRunner() {
        this.toggleFollowRunnerButton.addEventListener('click', () => {
            if (this.runners && this.runnerMarkers.get(this.runners[0].startNumber)?.getPosition()) {
                this.googleMap.setZoom(18);
                this.toggleFollowRunner();
            } else {
                this.setBounds();
            }
        });
    }

    initGeolocationMarker() {
        const activateButton = document.querySelector('.js-race-tracking-activate-geolocation');
        if (!navigator.geolocation) {
            activateButton.parentElement.removeChild(activateButton);
        }

        activateButton.addEventListener('click', this.toggleGeolocation.bind(this));

        this.geolocationMarker = new google.maps.Marker({
            map: this.googleMap,
            icon: {
                url: `${window.config.themePath}/assets/images/marker/geolocation.png`,
                scaledSize: new google.maps.Size(40, 40),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(20, 20),
            },
            zIndex: 101,
            visible: false,
            position: this.waypointMarkers[this.waypointMarkers.length - 1].waypoint.position,
        });
    }

    toggleGeolocation() {
        if (!navigator.geolocation) return;

        navigator.geolocation.watchPosition(
            ({coords}) => {
                this.googleMap.setZoom(17);
                this.updateGeolocationMarker({
                    lat: coords.latitude,
                    lng: coords.longitude,
                });
            },
        );

        this.showGeolocationButton = true;
        this.toggleFollow(this.FOLLOW_GEOLOCATION);
    }

    updateGeolocationMarker(position: google.maps.LatLngLiteral) {
        if (!this.geolocationMarker || !position) return;

        this.lastGeolocationPosition = position;

        if (this.showGeolocationButton) {
            this.geolocationMarker.setPosition(position);
            this.geolocationMarker.setVisible(true);
        } else {
            this.geolocationMarker.setVisible(false);
        }

        this.animateCameraFollow();
    }

    toggleFollowRunner(force?: boolean) {
        let active;

        if (force !== undefined) {
            this.toggleFollow(force ? this.FOLLOW_RUNNER : this.FOLLOW_DISABLED);
            active = force;
        } else {
            active = this.toggleFollow(this.FOLLOW_RUNNER);
        }

        this.toggleFollowRunnerButton.classList.toggle('race-tracking__toggle--active', active);
    }

    toggleFollow(follow: string): boolean {
        this.follow = this.follow === follow ? this.FOLLOW_DISABLED : follow;

        this.animateCameraFollow();

        return this.follow === follow;
    }

    animateCameraFollow() {
        if (this.follow === this.FOLLOW_RUNNER) {
            this.googleMap.panTo(this.runnerMarkers.get(this.runners[0].startNumber).getPosition());
        }

        if (this.follow === this.FOLLOW_GEOLOCATION && this.lastGeolocationPosition) {
            this.googleMap.panTo(this.lastGeolocationPosition);
        }
    }

    unsubscribe() {
        if (this.updateRunnerMarkersInterval) {
            clearInterval(this.updateRunnerMarkersInterval);
        }
    }

    updateRunnerMarkerPositions() {
        this.runners.forEach((runner) => {
            if (!runner) return;

            const runnerMarker = this.runnerMarkers.get(runner.startNumber);
            if (runnerMarker) {
                const newPosition = this.getRunnerPosition(runner);
                runnerMarker.setPosition(newPosition);
            }
        });
    }

    updateVisibleRunnerMarkers() {
        this.runners.forEach((runner) => {
            if (!runner) return;

            const runnerMarker = this.runnerMarkers.get(runner.startNumber);
            if (runnerMarker) {
                runnerMarker.setVisible(this.shouldRunnerBeVisible(runner));
            }
        });
    }

    getRunnerPosition(runner: Runner): google.maps.LatLng {
        return this.pathProvider.getRunnerPositionByDistanceInterpolated(runner);
    }

    shouldRunnerBeVisible(runner: Runner): boolean {
        if (!this.favoriteRunners.getState().favoriteRunner) {
            return false;
        }

        if (!runner.isRunning()) {
            return false;
        }

        return runner.startNumber === this.favoriteRunners.getState().favoriteRunner.startNumber;
    }

    matchRunnerMarkersWithRunners() {
        const removeMarkers = Array.from(this.runnerMarkers.keys())
            .filter((startNumber) => !this.runners.some((runner) => runner.startNumber === startNumber));

        removeMarkers.forEach((startNumber) => {
            const runnerMarker = this.runnerMarkers.get(startNumber);
            runnerMarker.setMap(null);
            this.runnerMarkers.delete(startNumber);
        });

        if (!this.runners) {
            return Promise.resolve();
        }

        return Promise.all(this.runners.map((runner) => {
            if (
                runner.hasDetailedData()
                && !this.runnerMarkers.get(runner.startNumber)
                && !this.runnerMarkersLock.get(runner.startNumber)
            ) {
                return this.addRunnerMarker(runner);
            }

            return Promise.resolve();
        }));
    }

    async addRunnerMarker(runner: Runner) {
        this.runnerMarkersLock.set(runner.startNumber, true);

        const icon = await RunnerIconProvider.getIcon(runner);
        const runnerMarker = new google.maps.Marker({
            map: this.googleMap,
            icon,
            zIndex: 100,
            visible: this.shouldRunnerBeVisible(runner),
            position: this.getRunnerPosition(runner),
        });

        this.runnerMarkers.set(runner.startNumber, runnerMarker);
        this.runnerMarkersLock.set(runner.startNumber, false);
    }
}

export default function initRaceMap() {
    if (!window.config || !window.config.googleMapsApiKey) {
        return Promise.reject();
    }

    return new Promise((resolve, reject) => {
        const loader = new Loader({
            apiKey: window.config.googleMapsApiKey,
            version: 'weekly',
            libraries: ['geometry'],
        });

        loader.importLibrary('maps')
            .then(({Map: GoogleMap}) => {
                const map = new RaceMap(GoogleMap);
                try {
                    map.init();
                    resolve(map);
                } catch (e) {
                    reject(e);
                }
            });
    });
}
